import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Wrench, Users, Award, CheckCircle, TrendingUp } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const CareerSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: '',
    level: '',
    experience: '',
    message: ''
  });

  const positions = [
    {
      id: 'ac-technician',
      title: 'AC Technician',
      levels: ['Helper', 'Junior Technician', 'Senior Technician', 'Expert Technician']
    },
    {
      id: 'accounts-social',
      title: 'Accounts, Social Media and Customer Management',
      levels: ['Fresher', 'Junior', 'Senior', 'Experienced']
    },
    {
      id: 'hvac-manager',
      title: 'HVAC Team Manager/Technician',
      levels: ['Chiller Team', 'VRF Team', 'Ductable Team', 'Cassette & Split Team']
    },
    {
      id: 'sales-marketing',
      title: 'Sales Marketing',
      levels: ['Bachelors', 'Masters', 'Fresher', 'Internship', 'Experienced']
    }
  ];

  const [selectedPosition, setSelectedPosition] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'position') {
      const pos = positions.find(p => p.id === value);
      setSelectedPosition(pos);
      setFormData(prev => ({ ...prev, level: '' }));
    }
  };

  const getWhatsAppMessage = () => {
    const positionTitle = positions.find(p => p.id === formData.position)?.title || '';
    return `*JOB APPLICATION - MARCO AIR CONDITION*

*Position Applied:* ${positionTitle}
*Level/Team:* ${formData.level}

*Applicant Details:*
Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}
Experience: ${formData.experience}

*Additional Information:*
${formData.message}`;
  };

  return (
    <section id="careers" className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900" data-testid="career-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">Join Our Team</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="career-title">
            Career Opportunities
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Join MARCO AIR CONDITION and be part of a team with 45+ years of excellence in HVAC industry
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Open Positions */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Open Positions</h3>
            <div className="space-y-4">
              {positions.map((position, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-amber-500/20 hover:border-amber-500/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-amber-500 to-yellow-500 p-3 rounded-lg">
                      {index === 0 && <Wrench className="h-6 w-6 text-black" />}
                      {index === 1 && <Users className="h-6 w-6 text-black" />}
                      {index === 2 && <Award className="h-6 w-6 text-black" />}
                      {index === 3 && <TrendingUp className="h-6 w-6 text-black" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-2">{position.title}</h4>
                      <div className="flex flex-wrap gap-2">
                        {position.levels.map((level, idx) => (
                          <span
                            key={idx}
                            className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full"
                          >
                            {level}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-amber-500/20">
              <h4 className="text-lg font-semibold text-white mb-4">Why Work With Us?</h4>
              <ul className="space-y-3">
                {[
                  'Competitive salary packages',
                  'Professional growth opportunities',
                  'Training and skill development',
                  'Work with premium HVAC brands',
                  'Supportive team environment'
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="h-5 w-5 text-amber-500" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Application Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Apply Now</h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <Input
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="career-form-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="career-form-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <Input
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="career-form-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position Applied For *</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                  data-testid="career-form-position"
                >
                  <option value="">Select Position</option>
                  {positions.map((pos) => (
                    <option key={pos.id} value={pos.id}>{pos.title}</option>
                  ))}
                </select>
              </div>

              {selectedPosition && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedPosition.id === 'hvac-manager' ? 'Select Team *' : 'Select Level *'}
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                    data-testid="career-form-level"
                  >
                    <option value="">
                      {selectedPosition.id === 'hvac-manager' ? 'Select Team' : 'Select Level'}
                    </option>
                    {selectedPosition.levels.map((level, idx) => (
                      <option key={idx} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                <Input
                  name="experience"
                  placeholder="e.g., 2 years, Fresher"
                  value={formData.experience}
                  onChange={handleChange}
                  data-testid="career-form-experience"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                <Textarea
                  name="message"
                  placeholder="Tell us about your skills, qualifications, and why you want to join MARCO AIR CONDITION..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  data-testid="career-form-message"
                />
              </div>

              {/* WhatsApp Submit Button */}
              <a
                href={getWhatsAppLink(getWhatsAppMessage())}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-all"
                data-testid="career-form-whatsapp"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send Application via WhatsApp
              </a>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareerSection;
